/**
 * SA-459 (workflow UX sweep spec §6.1–6.2): the step drawer's words,
 * beside the component — the `mapText.ts` / `processEventText.ts`
 * convention, which keeps `StepDrawer.tsx` exporting components only.
 */
import type { ProcessStepVM, StepDetailVM } from '@/utils/workflowOverview'
import type { Hue } from './executorTone'
import { formatConfidence } from './proposalText'

/** The level word's sentence (spec §6.2, the Who row). */
export const LEVEL_SENTENCE: Record<string, string> = {
  manual: 'A person does it. The form opens as a task; the AI is not involved',
  assisted: 'The AI proposes, a person confirms',
  automatic: 'The AI or the connector completes it; exceptions open a task',
}

export interface WhoChip {
  text: string
  /** A chip in this hue; `null` = muted text beside the chips (a wait's event, an automation's capability). */
  hue: Hue | null
  /** Drawn as an outline chip: an AI declared but not turned on. */
  outline?: boolean
}

/** "feasibility recorded" / "2 business days" — what a wait waits for. */
export function waitWords(wait: StepDetailVM['wait']): string | null {
  if (!wait) return null
  return wait.event ?? wait.after
}

/** The header's who-chips, which change with the level (spec §6.1). */
export function whoChips(
  step: Pick<ProcessStepVM, 'executorType' | 'level' | 'who' | 'party'>,
  detail: Pick<StepDetailVM, 'seatLabel' | 'automation' | 'wait'>,
): WhoChip[] {
  const seat = detail.seatLabel ?? step.who
  switch (step.executorType) {
    case 'person':
    case 'ai': {
      if (step.level === 'assisted') return [{ text: 'AI proposes', hue: 'ai' }, { text: `${seat} confirms`, hue: 'person' }]
      if (step.level === 'automatic') return [{ text: 'AI or automation', hue: 'ai' }, { text: `exceptions to ${seat}`, hue: 'person' }]
      return step.executorType === 'ai'
        ? [{ text: seat, hue: 'person' }, { text: 'AI declared, not turned on', hue: 'ai', outline: true }]
        : [{ text: seat, hue: 'person' }]
    }
    case 'automation':
      return [{ text: 'Automation', hue: 'automation' }, ...(detail.automation ? [{ text: detail.automation.capability, hue: null }] : [])]
    case 'wait': {
      const words = waitWords(detail.wait)
      return [{ text: 'Wait', hue: 'automation' }, ...(words ? [{ text: words, hue: null }] : [])]
    }
    case 'conversation':
      // SA-469 (spec D7): keyed on the party — a staff agenda is the intake.
      return [{ text: step.party === 'customer' ? 'Customer conversation' : 'Intake', hue: 'conversation' }]
  }
}

/** "80%" — the AI threshold as the SA-407 formatter prints a confidence, so a chip and a threshold never disagree. */
export function thresholdText(threshold: number): string {
  return formatConfidence(threshold) ?? '—'
}

/** "need more" — an exit's outcome word. */
export function exitWord(name: string): string {
  return name.replace(/_/g, ' ')
}
